import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Wrench, Plus, Eye, Building2, ClipboardList } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Table, Column } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import { api, getPropertyCoverImageUrl, PROPERTY_IMAGE_PLACEHOLDER } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';
import { formatDate } from '../../utils/date';

export type MaintenanceStatus = 'pending' | 'in_progress' | 'completed';
export type MaintenanceType = 'general' | 'desazolve' | 'impermeabilizacion' | 'preventivo' | 'correctivo';

export const MAINTENANCE_TYPE_CONFIG: Record<MaintenanceType, { label: string; className: string }> = {
  general:          { label: 'General',           className: 'bg-gray-100 text-gray-700' },
  desazolve:        { label: 'Desazolve',         className: 'bg-blue-100 text-blue-800' },
  impermeabilizacion: { label: 'Impermeabilización', className: 'bg-cyan-100 text-cyan-800' },
  preventivo:       { label: 'Preventivo',        className: 'bg-purple-100 text-purple-800' },
  correctivo:       { label: 'Correctivo',        className: 'bg-orange-100 text-orange-800' },
};

export interface PropertyMaintenanceRow {
  id: string;
  propertyId: string;
  propertyName?: string;
  propertyCoverImage?: string;
  propertyAddress?: string;
  title: string;
  description?: string;
  type: MaintenanceType;
  performedBy: string;
  performedAt: string;
  cost?: number;
  status: MaintenanceStatus;
  notes?: string;
  images?: string[];
  createdAt?: string;
}

const limit = 20;

function mapApiToRow(item: any): PropertyMaintenanceRow {
  const performedAt = item.performedAt
    ? (typeof item.performedAt === 'string' ? item.performedAt : new Date(item.performedAt).toISOString().split('T')[0])
    : '';
  const prop = item.properties ?? item.property;
  return {
    id: item.id,
    propertyId: item.propertyId,
    propertyName: prop?.name,
    propertyCoverImage: prop?.coverImage,
    propertyAddress: prop?.address,
    title: item.title,
    description: item.description,
    type: (item.type || 'general') as MaintenanceType,
    performedBy: item.performedBy,
    performedAt,
    cost: item.cost != null ? Number(item.cost) : undefined,
    status: (item.status || 'pending') as MaintenanceStatus,
    notes: item.notes,
    images: Array.isArray(item.images) ? item.images : [],
    createdAt: item.createdAt,
  };
}

function TypeBadge({ type }: { type: MaintenanceType }) {
  const { label, className } = MAINTENANCE_TYPE_CONFIG[type] ?? MAINTENANCE_TYPE_CONFIG.general;
  return (
    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${className}`}>
      {label}
    </span>
  );
}

function StatusBadge({ status }: { status: MaintenanceStatus }) {
  const config: Record<MaintenanceStatus, { label: string; className: string }> = {
    pending: { label: 'Pendiente', className: 'bg-amber-100 text-amber-800' },
    in_progress: { label: 'En progreso', className: 'bg-blue-100 text-blue-800' },
    completed: { label: 'Completado', className: 'bg-green-100 text-green-800' },
  };
  const { label, className } = config[status] ?? config.pending;
  return (
    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${className}`}>
      {label}
    </span>
  );
}

export const PropertyManagementList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [reportsPage, setReportsPage] = useState(1);
  const [propertyFilter, setPropertyFilter] = useState<string>('');
  const [reportPropertyFilter, setReportPropertyFilter] = useState<string>('');
  const [reportStatusFilter, setReportStatusFilter] = useState<string>('');
  const [reporterFilter, setReporterFilter] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    propertyId: '',
    title: '',
    description: '',
    assignedUserId: '',
    unitId: '',
    notes: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  const { hasPermission } = usePermissions();
  const canCreateMaintenance =
    hasPermission('create_property_management') ||
    hasPermission('manage_property_management') ||
    hasPermission('create_maintenance_reports') ||
    hasPermission('manage_maintenance_reports');

  const { data: maintenancesRes, isLoading: loading, error: queryError } = useQuery({
    queryKey: ['property-maintenances', page, propertyFilter || undefined],
    queryFn: () =>
      api.getPropertyMaintenances({
        page,
        limit,
        ...(propertyFilter ? { propertyId: propertyFilter } : {}),
      }),
    staleTime: 60_000,
  });

  const { data: propertiesRes } = useQuery({
    queryKey: ['properties', 'list'],
    queryFn: () => api.getProperties({ limit: 500 }),
    staleTime: 60_000,
    enabled: isModalOpen || true,
  });

  const { data: maintenanceUsers = [] } = useQuery({
    queryKey: ['users', 'by-role', 'Mantenimiento'],
    queryFn: () => api.getMaintenanceUsers(),
    staleTime: 5 * 60_000,
    enabled: isModalOpen && canCreateMaintenance,
  });

  const { data: unitsForProperty = [] } = useQuery({
    queryKey: ['units', 'property-management-new-report', formData.propertyId],
    queryFn: async () => {
      const res = await api.getUnits(formData.propertyId, { limit: 500 });
      return Array.isArray(res) ? res : (res?.data ?? []);
    },
    enabled: !!formData.propertyId && isModalOpen,
    staleTime: 60_000,
  });

  const maintenances: PropertyMaintenanceRow[] = useMemo(() => {
    const list = maintenancesRes?.data ?? [];
    return Array.isArray(list) ? list.map(mapApiToRow) : [];
  }, [maintenancesRes]);
  const total = maintenancesRes?.total ?? 0;
  const totalPages = maintenancesRes?.totalPages ?? 0;
  const properties = useMemo(() => (propertiesRes?.data ?? []).map((p: any) => ({ id: p.id, name: p.name })), [propertiesRes]);

  // Reportes de mantenimiento (maintenance_reports)
  const {
    data: reportsRes,
    isLoading: reportsLoading,
    error: reportsQueryError,
  } = useQuery({
    queryKey: ['all-maintenance-reports', reportsPage, reportPropertyFilter || undefined, reportStatusFilter || undefined],
    queryFn: () =>
      api.getAllMaintenanceReports({
        page: reportsPage,
        limit,
        ...(reportPropertyFilter ? { propertyId: reportPropertyFilter } : {}),
        ...(reportStatusFilter ? { status: reportStatusFilter } : {}),
      }),
    staleTime: 60_000,
  });

  const allReportsRaw: any[] = Array.isArray(reportsRes?.data) ? reportsRes!.data : [];
  const reporters = useMemo(() => {
    const map = new Map<string, string>();
    allReportsRaw.forEach((r: any) => {
      if (r.users?.id && r.users?.name) {
        map.set(r.users.id, r.users.name);
      }
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [allReportsRaw]);

  const filteredReports = useMemo(() => {
    return allReportsRaw.filter((r: any) => !reporterFilter || r.users?.id === reporterFilter);
  }, [allReportsRaw, reporterFilter]);

  const reportsTotal = reportsRes?.total ?? 0;
  const reportsTotalPages = reportsRes?.totalPages ?? 0;
  const reportsError =
    reportsQueryError != null
      ? (reportsQueryError instanceof Error ? reportsQueryError.message : String(reportsQueryError))
      : null;

  const createMutation = useMutation({
    mutationFn: (payload: typeof formData & { images?: string[] }) => {
      const findings = payload.description.trim() || '—';
      return api.createMaintenanceReport({
        propertyId: payload.propertyId,
        title: payload.title.trim(),
        findings,
        assignedUserId: payload.assignedUserId,
        ...(payload.unitId ? { unitId: payload.unitId } : {}),
        notes: payload.notes?.trim() || undefined,
        images: payload.images,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-maintenances'] });
      queryClient.invalidateQueries({ queryKey: ['all-maintenance-reports'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-reports'] });
      setIsModalOpen(false);
      setFormData({
        propertyId: '',
        title: '',
        description: '',
        assignedUserId: '',
        unitId: '',
        notes: '',
      });
      setSelectedFiles([]);
      setPreviews([]);
      setFormError(null);
    },
    onError: (err: Error) => setFormError(err.message || 'Error al crear'),
  });

  const error =
    queryError != null
      ? (queryError instanceof Error ? queryError.message : String(queryError))
      : null;

  const handleOpenModal = () => {
    setFormError(null);
    setSelectedFiles([]);
    setPreviews([]);
    setFormData({
      propertyId: propertyFilter || '',
      title: '',
      description: '',
      assignedUserId: '',
      unitId: '',
      notes: '',
    });
    setIsModalOpen(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setSelectedFiles((prev) => [...prev, ...files]);
    files.forEach((f) => {
      const reader = new FileReader();
      reader.onload = () => setPreviews((prev) => [...prev, reader.result as string]);
      reader.readAsDataURL(f);
    });
    e.target.value = '';
  };

  const removeFile = (idx: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== idx));
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.propertyId || !formData.title.trim() || !formData.assignedUserId.trim()) {
      setFormError('Completa propiedad, título y técnico asignado.');
      return;
    }
    setFormError(null);
    let imageUrls: string[] = [];
    try {
      if (selectedFiles.length > 0) {
        setUploading(true);
        const res = await api.uploadMaintenanceReportImages(selectedFiles);
        imageUrls = res.fileUrls ?? [];
      }
      await createMutation.mutateAsync({
        ...formData,
        ...(imageUrls.length > 0 ? { images: imageUrls } : {}),
      });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Error al crear');
    } finally {
      setUploading(false);
    }
  };

  const columns: Column<PropertyMaintenanceRow>[] = [
    {
      header: 'Propiedad',
      accessor: (row) => (
        <div className="flex items-center gap-3">
          <img
            src={row.propertyCoverImage ? getPropertyCoverImageUrl(row.propertyCoverImage) : PROPERTY_IMAGE_PLACEHOLDER}
            alt=""
            className="w-10 h-10 rounded object-cover flex-shrink-0"
          />
          <div>
            <div className="font-medium text-gray-900">{row.propertyName || '—'}</div>
            {row.propertyAddress && (
              <div className="text-xs text-gray-500 truncate max-w-[180px]">{row.propertyAddress}</div>
            )}
          </div>
        </div>
      ),
    },
    { header: 'Título', accessor: (row) => row.title },
    { header: 'Tipo', accessor: (row) => <TypeBadge type={row.type} /> },
    { header: 'Asignado a', accessor: (row) => row.performedBy },
    {
      header: 'Fecha',
      accessor: (row) => (row.performedAt ? formatDate(row.performedAt) : '—'),
    },
    {
      header: 'Estado',
      accessor: (row) => <StatusBadge status={row.status} />,
    },
    {
      header: '',
      accessor: (row) => (
        <button
          type="button"
          onClick={() => navigate(`/property-management/${row.id}`)}
          className="inline-flex items-center justify-center p-2 rounded-lg text-sm font-medium bg-white text-primary border border-gray-200 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          title="Ver mantenimiento"
          aria-label="Ver mantenimiento"
        >
          <Eye className="w-4 h-4" />
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Wrench className="w-7 h-7 text-primary" />
          Administración de inmuebles
        </h1>
        {canCreateMaintenance && (
          <Button onClick={handleOpenModal} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Nuevo mantenimiento
          </Button>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <Building2 className="w-4 h-4" />
            Filtrar por propiedad
          </label>
          <select
            value={propertyFilter}
            onChange={(e) => {
              setPropertyFilter(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm min-w-[200px] focus:ring-2 focus:ring-primary/20 focus:border-primary"
          >
            <option value="">Todas</option>
            {properties.map((p: { id: string; name: string }) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="mx-4 mt-3 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            <Table data={maintenances} columns={columns} />
            {totalPages > 1 && (
              <Pagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>

      {/* Reportes de mantenimiento (maintenance_reports) */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center gap-3">
          <span className="font-semibold text-gray-900 text-sm flex items-center gap-2">
            <ClipboardList className="w-4 h-4" />
            Reportes de mantenimiento
          </span>
          {/* Filtros */}
          <div className="flex flex-wrap items-center gap-3 ml-auto">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <Building2 className="w-4 h-4" />
              Propiedad
              <select
                value={reportPropertyFilter}
                onChange={(e) => {
                  setReportPropertyFilter(e.target.value);
                  setReportsPage(1);
                }}
                className="ml-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm min-w-[160px] focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="">Todas</option>
                {properties.map((p: { id: string; name: string }) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              Estado
              <select
                value={reportStatusFilter}
                onChange={(e) => {
                  setReportStatusFilter(e.target.value);
                  setReportsPage(1);
                }}
                className="ml-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm min-w-[140px] focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="">Todos</option>
                <option value="pending">Pendiente</option>
                <option value="in_progress">En curso</option>
                <option value="reviewed">Revisado</option>
                <option value="resolved">Resuelto</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              Reportado por
              <select
                value={reporterFilter}
                onChange={(e) => {
                  setReporterFilter(e.target.value);
                  setReportsPage(1);
                }}
                className="ml-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm min-w-[160px] focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="">Todos</option>
                {reporters.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {reportsError && (
          <div className="mx-4 mt-3 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{reportsError}</div>
        )}

        {reportsLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            <Table
              data={filteredReports}
              columns={[
                {
                  header: 'Propiedad',
                  accessor: (row: any) => (
                    <div className="flex items-center gap-3">
                      <img
                        src={
                          row.properties?.coverImage
                            ? getPropertyCoverImageUrl(row.properties.coverImage)
                            : PROPERTY_IMAGE_PLACEHOLDER
                        }
                        alt=""
                        className="w-10 h-10 rounded object-cover flex-shrink-0"
                      />
                      <div>
                        <div className="font-medium text-gray-900">
                          {row.properties?.name || '—'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {row.units?.unitNumber ? `Unidad ${row.units.unitNumber}` : '—'}
                        </div>
                      </div>
                    </div>
                  ),
                },
                { header: 'Título', accessor: (row: any) => row.title },
                { header: 'Reportado por', accessor: (row: any) => row.users?.name || '—' },
                {
                  header: 'Fecha',
                  accessor: (row: any) =>
                    row.createdAt ? formatDate(row.createdAt) : '—',
                },
                {
                  header: 'Estado',
                  accessor: (row: any) => {
                    const map: Record<string, { label: string; className: string }> = {
                      pending: { label: 'Pendiente', className: 'bg-amber-100 text-amber-800' },
                      in_progress: { label: 'En curso', className: 'bg-sky-100 text-sky-800' },
                      reviewed: { label: 'Revisado', className: 'bg-blue-100 text-blue-800' },
                      resolved: { label: 'Resuelto', className: 'bg-green-100 text-green-800' },
                    };
                    const cfg = map[row.status] ?? map.pending;
                    return (
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${cfg.className}`}>
                        {cfg.label}
                      </span>
                    );
                  },
                },
                {
                  header: '',
                  accessor: (row: any) => (
                    <button
                      type="button"
                      onClick={() => navigate(`/property-management/report/${row.id}`)}
                      className="inline-flex items-center justify-center p-2 rounded-lg text-sm font-medium bg-white text-primary border border-gray-200 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                      title="Ver reporte"
                      aria-label="Ver reporte"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  ),
                },
              ]}
            />
            {reportsTotalPages > 1 && (
              <Pagination
                page={reportsPage}
                totalPages={reportsTotalPages}
                total={reportsTotal}
                limit={limit}
                onPageChange={setReportsPage}
              />
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedFiles([]);
          setPreviews([]);
          setFormError(null);
        }}
        title="Nuevo mantenimiento"
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {formError && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{formError}</div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Propiedad *</label>
            <select
              value={formData.propertyId}
              onChange={(e) => setFormData((d) => ({ ...d, propertyId: e.target.value, unitId: '' }))}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="">Selecciona una propiedad</option>
              {(propertiesRes?.data ?? []).map((p: any) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData((d) => ({ ...d, title: e.target.value }))}
              placeholder="Ej. Reparación de tubería"
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData((d) => ({ ...d, description: e.target.value }))}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unidad (opcional)</label>
            <select
              value={formData.unitId}
              onChange={(e) => setFormData((d) => ({ ...d, unitId: e.target.value }))}
              disabled={!formData.propertyId}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:bg-gray-100 disabled:text-gray-500"
            >
              <option value="">Toda la propiedad / sin especificar</option>
              {unitsForProperty.map((u: { id: string; unitNumber?: string }) => (
                <option key={u.id} value={u.id}>
                  Unidad {u.unitNumber ?? u.id.slice(0, 8)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Asignar a *</label>
            <select
              value={formData.assignedUserId}
              onChange={(e) => setFormData((d) => ({ ...d, assignedUserId: e.target.value }))}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="">Seleccionar técnico...</option>
              {maintenanceUsers.map((user: { id: string; name: string }) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Se crea un reporte de mantenimiento asignado al técnico (visible en /maintenance).
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData((d) => ({ ...d, notes: e.target.value }))}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fotos / Evidencia</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-primary/10 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary"
            />
            {previews.length > 0 && (
              <div className="flex gap-2 flex-wrap mt-2">
                {previews.map((src, i) => (
                  <div key={i} className="relative">
                    <img src={src} alt="" className="w-20 h-20 object-cover rounded border border-gray-200" />
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center"
                      aria-label="Quitar imagen"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setIsModalOpen(false);
                setSelectedFiles([]);
                setPreviews([]);
                setFormError(null);
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={createMutation.isPending || uploading}>
              {uploading || createMutation.isPending ? 'Guardando...' : 'Crear'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
